import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { DaypartDay, User, Vehicle } from 'src/app/interfaces';
import {
  arrayUnion,
  arrayRemove,
  getDoc,
  doc,
  query,
  collection,
  where,
  getDocs,
  setDoc,
  onSnapshot,
  orderBy,
} from 'firebase/firestore';
import { ConfirmChangeAvailabilityComponent } from '../../dialogs/confirm-change-availability/confirm-change-availability.component';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { auth, db, functions } from 'src/app/app.component';
import { httpsCallable } from 'firebase/functions';
import { Role } from 'src/app/enums';
import { MatCard, MatCardContent } from '@angular/material/card';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll } from '@angular/cdk/scrolling';
import { MatSlideToggle } from '@angular/material/slide-toggle';

interface VehicleExtended extends Vehicle {
  checked?: boolean;
}

@Component({
  selector: 'app-availability-vehicles',
  templateUrl: './availability-vehicles.component.html',
  styleUrls: ['./availability-vehicles.component.scss'],
  imports: [
    MatCard,
    MatCardContent,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    MatSlideToggle,
  ],
})
export class AvailabilityVehiclesComponent implements OnInit, OnDestroy {
  vehicles = signal<VehicleExtended[]>([]);
  organisationId = localStorage.getItem('orgId');

  userId: string | null = null;
  userIdFromAdmin: string | null = null;
  changerName: string | null = null;

  @Input() set getUserId(userId: string) {
    this.userIdFromAdmin = userId ? userId : null;
    this.getVehicles();
    // console.log('value', value);
  }
  private _type: Role | null = null;
  @Input() set type(value: Role) {
    this._type = value;
    this.getVehicles();
  }
  get type() {
    return this._type!;
  }
  @Input() userCanModify: boolean = false;

  get vehicleFieldName() {
    return this.type === Role.CoDriver ? 'vehiclesToCoRide' : 'vehiclesToRide';
  }

  private unsubscribeUser?: () => void;
  private unsubscribeVehicles?: () => void;

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    this.getVehicles();
  }

  ngOnDestroy(): void {
    if (this.unsubscribeUser) {
      this.unsubscribeUser();
    }
    if (this.unsubscribeVehicles) {
      this.unsubscribeVehicles();
    }
  }

  async toggleChange($event: any, vehicleId: string) {
    // console.log('id', $event);
    if ($event.checked) {
      setDoc(
        doc(db, `organisations/${this.organisationId}/users/${this.userId}`),
        {
          [this.vehicleFieldName]: arrayUnion(vehicleId),
        },
        { merge: true },
      );
    } else {
      const today = new Date();
      let filteredRides = [];

      const user = (
        await getDoc(doc(db, `organisations/${this.organisationId}/users/${this.userId}`))
      ).data() as User;
      const roles = user.roles || [];

      const baseQuery = collection(db, `organisations/${this.organisationId}/daypartDays`);
      const queries = [
        query(baseQuery, where('vehicleId', '==', vehicleId), where('driverId', '==', this.userId)),
        query(
          baseQuery,
          where('vehicleId', '==', vehicleId),
          where('coDriverId', '==', this.userId),
        ),
      ];

      const results = await Promise.all(queries.map((q) => getDocs(q)));
      const allDocs = results.flatMap((snapshot) => snapshot.docs);

      allDocs.forEach((daypartDay) => {
        const data = daypartDay.data() as DaypartDay;
        const rideDate = data.date?.toDate() || new Date(data.year!, data.month!, data.day!);

        if (moment(rideDate).isSameOrAfter(moment(today), 'day')) {
          filteredRides.push(daypartDay);
        }
      });

      if (filteredRides.length > 0) {
        const vehicle = this.vehicles().find((v) => v.id === vehicleId);
        const dialogRef = this.dialog.open(ConfirmChangeAvailabilityComponent, {
          width: '500px',
          disableClose: true,
          data: {
            availabilityType: 'vehicles',
            itemName: vehicle ? vehicle.name : '',
          },
        });
        dialogRef.afterClosed().subscribe(async (result) => {
          if (result) {
            if (auth.currentUser) {
              this.changerName =
                (
                  await getDoc(
                    doc(db, `organisations/${this.organisationId}/users/${auth.currentUser.uid}`),
                  )
                ).data()?.name ?? '';
            }
            const user = (
              await getDoc(doc(db, `organisations/${this.organisationId}/users/${this.userId}`))
            ).data() as User;
            setDoc(
              doc(db, `organisations/${this.organisationId}/users/${this.userId}`),
              {
                [this.vehicleFieldName]: arrayRemove(vehicleId),
              },
              { merge: true },
            );
            try {
              const callBody = {
                orgId: this.organisationId,
                userName: 'planner',
                mailType: 'scheduleVehicles',
                emailTo: 'self',
                optionalData: {
                  date: new Date(),
                  changedBy: this.changerName,
                  userChanged: user.name,
                  userRole: this.type === Role.Driver ? 'chauffeur' : 'bijrijder',
                  mailPlanners: true,
                },
              };
              const callable = httpsCallable(functions, 'httpSendMail');
              const callRes = await callable(callBody);
              console.log(callRes);
            } catch (error) {
              console.log(error);
            }
          } else {
            $event.source.checked = true;
          }
        });
      } else {
        setDoc(
          doc(db, `organisations/${this.organisationId}/users/${this.userId}`),
          {
            [this.vehicleFieldName]: arrayRemove(vehicleId),
          },
          { merge: true },
        );
      }
    }
  }

  getVehicles() {
    if (auth.currentUser) {
      this.userId = this.userIdFromAdmin ? this.userIdFromAdmin : auth.currentUser.uid;

      if (this.unsubscribeUser) this.unsubscribeUser();
      if (this.unsubscribeVehicles) this.unsubscribeVehicles();

      // Clear current list to prevent flashing old data from another role
      this.vehicles.set([]);

      let latestVehiclesRaw: any[] = [];
      let latestUserRaw: User | null = null;

      const updateSignal = () => {
        if (!latestUserRaw) return;
        const userVehicleIds = latestUserRaw[this.vehicleFieldName] ?? [];
        const mapped = latestVehiclesRaw
          .filter((vehicle) => vehicle.vehicleType !== 'private')
          .map((v) => {
            const vehicle = { ...v } as VehicleExtended;
            vehicle.checked = vehicle.id ? userVehicleIds.includes(vehicle.id) : false;
            return vehicle;
          });
        this.vehicles.set(
          mapped.sort(
            (a, b) =>
              (a.calendarStyle?.sortingNumber ?? Number.MAX_SAFE_INTEGER) -
              (b.calendarStyle?.sortingNumber ?? Number.MAX_SAFE_INTEGER),
          ),
        );
      };

      this.unsubscribeVehicles = onSnapshot(
        query(
          collection(db, `organisations/${this.organisationId}/vehicles`),
          where('active', '==', true),
          orderBy('name'),
        ),
        (snapshot) => {
          latestVehiclesRaw = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
          updateSignal();
        },
      );

      this.unsubscribeUser = onSnapshot(
        doc(db, `organisations/${this.organisationId}/users/${this.userId}`),
        (snapshot) => {
          latestUserRaw = snapshot.data() as User;
          updateSignal();
        },
      );
    }
  }
}
